import BigNumber from 'bignumber.js';
import { formatPriceLevel } from '../../utils/pricePrecision';

// Shared number formatting for the Pocket views. Kept in one place so every
// market renders values identically (grouping, decimals, the "--" placeholder).
const O = { decimalSeparator: '.', groupSeparator: ',', groupSize: 3 };

// General value formatter. money → $1,234.56 · qty → integer or 2dp · lev → integer.
export const fmt = (v, { money = false, qty = false, lev = false, dp = 2 } = {}) => {
  if (v === '--' || v === undefined || v === null) return '--';
  let bn; try { bn = new BigNumber(v); } catch { return '--'; }
  if (bn.isNaN()) return '--';
  if (money) return `$${bn.toFormat(2, O)}`;
  if (qty) return bn.isInteger() ? bn.toFormat(0, O) : bn.toFormat(2, O);
  if (lev) return bn.toFormat(0, O);
  return bn.toFormat(dp, O);
};

// Price-level formatter. `decimals`, when the market knows its real tradeable
// precision (forex pip, futures tick), always wins; otherwise rounds adaptively
// by significant figures — see utils/pricePrecision.
export const fmtLvl = (v, decimals) => formatPriceLevel(v, decimals ?? null, '--');

// Pip / tick distance formatter — always 1 decimal.
export const fmtPips = (v) => {
  if (v === '--' || v == null) return '--';
  let bn; try { bn = new BigNumber(v); } catch { return '--'; }
  if (bn.isNaN()) return '--';
  return bn.toFormat(1, O);
};

// Raw input value (no forced decimals) — for echoing a field's value in a chip.
export const raw = (v) => {
  if (v === '--' || v == null || v === '') return '--';
  try { const n = new BigNumber(v); return n.isNaN() ? '--' : n.toString(); } catch { return String(v); }
};

// --- Compact display: abbreviate $-amounts >= $1000 to K/M/B (DISPLAY only; the
// copy/title always keep the full number). Prices, percentages and amounts under
// $1000 are left untouched. Used for output MONEY amounts and embedded sub-text.
const abbrOne = (mm) => {
  const m = String(mm).match(/^([+\-−]?)\$([\d,]+(?:\.\d+)?)$/);
  if (!m) return mm;
  const num = parseFloat(m[2].replace(/,/g, ''));
  if (isNaN(num) || num < 1000) return mm;
  const sign = m[1] || '';
  // Only strip trailing zeros that come after a decimal point (5.00 -> 5, 1.50 -> 1.5) —
  // must NOT touch whole numbers with no decimal point (130 must stay 130, not 13).
  const trim = (x) => x.includes('.') ? x.replace(/0+$/, '').replace(/\.$/, '') : x;
  let s;
  if (num >= 1e9) { const n = num / 1e9; s = trim(n >= 100 ? n.toFixed(0) : n.toFixed(2)) + 'B'; }
  else if (num >= 1e6) { const n = num / 1e6; s = trim(n >= 100 ? n.toFixed(0) : n.toFixed(2)) + 'M'; }
  else { const n = num / 1e3; s = trim(n >= 100 ? n.toFixed(0) : n.toFixed(2)) + 'K'; }
  return `${sign}$${s}`;
};

// Abbreviate any $-amounts found inside a string (for the gray inline sub-text).
export const abbrInline = (s) => String(s).replace(/\$[\d,]+(?:\.\d+)?/g, (mm) => abbrOne(mm));

// Money output amount → { full, disp }: full for copy/title, disp abbreviated for display.
export const money = (v) => { const full = fmt(v, { money: true }); return { full, disp: abbrOne(full) }; };
